import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import {
  checkmarkCircleOutline,
  informationCircleOutline,
  refreshOutline,
} from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

import {
  MiQRResponse,
  MiTemaItem,
  obtenerMiQR,
  obtenerMisTemas,
} from "src/services/entregasMasivas";

const MiQREntrega: React.FC = () => {
  const [info, setInfo] = useState<MiQRResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const [temas, setTemas] = useState<MiTemaItem[]>([]);
  const [temaId, setTemaId] = useState<number | null>(null);

  const tickRef = useRef<number | null>(null);
  const refreshRef = useRef<number | null>(null);

  const cargar = async (tid?: number | null) => {
    setError("");
    try {
      const id = tid === undefined ? temaId : tid;
      const data = await obtenerMiQR(id ?? undefined);
      setInfo(data);
      if (!data.recibido && data.ttl_seconds) {
        setSecondsLeft(data.ttl_seconds);
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.error ||
          "No se pudo generar el codigo QR. Intenta nuevamente."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Cargamos los temas vigentes del usuario para ofrecer un selector cuando
    // hay mas de una entrega activa a la vez.
    obtenerMisTemas()
      .then((lista) => {
        setTemas(lista);
        const inicial = lista[0]?.id ?? null;
        setTemaId(inicial);
        cargar(inicial);
      })
      .catch(() => cargar(null));
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      if (refreshRef.current) window.clearTimeout(refreshRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambiarTema = (id: number) => {
    setTemaId(id);
    setLoading(true);
    cargar(id);
  };

  // Countdown visual y refresco automatico al expirar la ventana.
  useEffect(() => {
    if (!info || info.recibido || !info.qr) return;
    if (tickRef.current) window.clearInterval(tickRef.current);
    if (refreshRef.current) window.clearTimeout(refreshRef.current);

    tickRef.current = window.setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    const ms = Math.max((info.ttl_seconds || 60) - 1, 1) * 1000;
    refreshRef.current = window.setTimeout(() => {
      cargar();
    }, ms);

    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      if (refreshRef.current) window.clearTimeout(refreshRef.current);
    };
  }, [info?.qr, info?.ttl_seconds, info?.recibido]);

  const tituloTema = info?.tema?.nombre || "Mi QR";

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>{tituloTema}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => cargar()} disabled={loading}>
              <IonIcon slot="icon-only" icon={refreshOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {temas.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <IonSelect
              value={temaId}
              interface="action-sheet"
              onIonChange={(e) => cambiarTema(e.detail.value)}
              style={{ border: "1px solid #ccc", borderRadius: 8, paddingLeft: 12 }}
            >
              {temas.map((t) => (
                <IonSelectOption key={t.id} value={t.id}>
                  {t.nombre}
                </IonSelectOption>
              ))}
            </IonSelect>
          </div>
        )}

        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <IonSpinner name="crescent" />
          </div>
        )}

        {!loading && error && (
          <div style={{ textAlign: "center", color: "#c62828", padding: 24 }}>
            {error}
            <div style={{ marginTop: 16 }}>
              <IonButton onClick={() => cargar()}>Reintentar</IonButton>
            </div>
          </div>
        )}

        {!loading && info && info.tema === null && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <IonIcon
              icon={informationCircleOutline}
              style={{ fontSize: 96, color: "#777" }}
            />
            <h2 style={{ marginTop: 16 }}>Sin entregas activas</h2>
            <p style={{ color: "#555" }}>
              {info.mensaje || "No hay una entrega activa en este momento."}
            </p>
          </div>
        )}

        {!loading && info?.recibido && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <IonIcon
              icon={checkmarkCircleOutline}
              style={{ fontSize: 96, color: "#2e7d32" }}
            />
            <h2 style={{ marginTop: 16 }}>Ya recibiste tu </h2>
            {info.tema?.descripcion && (
              <p style={{ color: "#444" }}>{info.tema.descripcion}</p>
            )}
            <p style={{ color: "#555" }}>
              {info.mensaje || "Agradecemos tu participacion."}
            </p>
            {info.entrega?.fecha_hora && (
              <p style={{ color: "#777", fontSize: 14 }}>
                Retirado el{" "}
                {new Date(info.entrega.fecha_hora).toLocaleString()}
              </p>
            )}
            {info.entrega?.entregado_por && (
              <p style={{ color: "#777", fontSize: 14 }}>
                Entregado por{" "}
                <b>
                  {info.entrega.entregado_por_nombre || info.entrega.entregado_por}
                </b>
              </p>
            )}
            {info.entrega?.metodo === "manual" && info.entrega?.motivo_manual && (
              <p style={{ color: "#777", fontSize: 14 }}>
                Registro manual: {info.entrega.motivo_manual}
              </p>
            )}
            {info.entrega?.lat != null && info.entrega?.lng != null && (
              <p style={{ fontSize: 14 }}>
                <a
                  href={`https://www.google.com/maps?q=${info.entrega.lat},${info.entrega.lng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Ver ubicacion de la entrega
                </a>
              </p>
            )}
          </div>
        )}

        {!loading && !info?.recibido && info && info.elegible === false && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <IonIcon
              icon={informationCircleOutline}
              style={{ fontSize: 96, color: "#f57c00" }}
            />
            <h2 style={{ marginTop: 16 }}>Aun no estas habilitado</h2>
            {info.tema?.descripcion && (
              <p style={{ color: "#444" }}>{info.tema.descripcion}</p>
            )}
            <p style={{ color: "#555" }}>
              {info.mensaje ||
                "Comunicate con el equipo organizador para que te habilite."}
            </p>
            <div style={{ marginTop: 16 }}>
              <IonButton onClick={() => cargar()}>Reintentar</IonButton>
            </div>
          </div>
        )}

        {!loading && !info?.recibido && info?.qr && (
          <div style={{ textAlign: "center", paddingTop: 8 }}>
            {info.tema?.descripcion && (
              <p style={{ color: "#444", marginBottom: 8 }}>
                {info.tema.descripcion}
              </p>
            )}
            <p style={{ marginBottom: 8 }}>
              Hola <b>{info.nombre || info.cedula}</b>
            </p>
            <p style={{ color: "#555", marginBottom: 16 }}>
              Mostra este codigo a la persona encargada de la entrega.
            </p>

            <div
              style={{
                display: "inline-block",
                padding: 16,
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: 12,
              }}
            >
              <QRCodeCanvas
                value={info.qr}
                size={260}
                level="M"
                includeMargin={false}
              />
            </div>

            <div style={{ marginTop: 16, color: "#666" }}>
              El codigo se actualiza en {secondsLeft}s
            </div>
            <div
              style={{
                marginTop: 4,
                color: "#999",
                fontSize: 12,
              }}
            >
              No compartas tu QR ni hagas captura de pantalla.
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default MiQREntrega;
